import { filterVideoBids } from '@platforms/shared';
import { context, DiProcess, InstantConfigService } from '@wikia/ad-engine';
import { Injectable } from '@wikia/dependency-injection';
import { getAppnexusContext } from '../../../bidders/prebid/appnexus';
import { getAppnexusAstContext } from '../../../bidders/prebid/appnexus-ast';
import { getIndexExchangeContext } from '../../../bidders/prebid/index-exchange';
import { getKargoContext } from '../../../bidders/prebid/kargo';
import { getMagniteS2sContext } from '../../../bidders/prebid/magniteS2s';
import { getMedianetContext } from '../../../bidders/prebid/medianet';
import { getNobidContext } from '../../../bidders/prebid/nobid';
import { getOpenXContext } from '../../../bidders/prebid/openx';
import { getOzoneContext } from '../../../bidders/prebid/ozone';
import { getPubmaticContext } from '../../../bidders/prebid/pubmatic';
import { getRoundelContext } from '../../../bidders/prebid/roundel';
import { getRubiconContext } from '../../../bidders/prebid/rubicon';
import { getRubiconDisplayContext } from '../../../bidders/prebid/rubicon-display';
import { getSeedtagContext } from '../../../bidders/prebid/seedtag';
import { getTripleliftContext } from '../../../bidders/prebid/triplelift';
import { getVerizonContext } from '../../../bidders/prebid/verizon';
import { getWebadsContext } from '../../../bidders/prebid/webads';
import { getWikiaContext } from '../../../bidders/prebid/wikia';
import { getWikiaVideoContext } from '../../../bidders/prebid/wikia-video';

@Injectable()
export class UcpDesktopPrebidConfigSetup implements DiProcess {
	constructor(private instantConfig: InstantConfigService) {}

	execute(): void {
		const icMagniteS2sVideo = !!this.instantConfig.get<boolean>('icMagniteS2sVideo');

		context.set('bidders.prebid.appnexus', filterVideoBids(getAppnexusContext()));
		context.set('bidders.prebid.appnexusAst', filterVideoBids(getAppnexusAstContext()));
		context.set('bidders.prebid.ix', filterVideoBids(getIndexExchangeContext()));
		context.set('bidders.prebid.kargo', filterVideoBids(getKargoContext()));
		context.set('bidders.prebid.medianet', filterVideoBids(getMedianetContext()));
		context.set('bidders.prebid.mgnipbs', filterVideoBids(getMagniteS2sContext(icMagniteS2sVideo)));
		context.set('bidders.prebid.nobid', filterVideoBids(getNobidContext()));
		context.set('bidders.prebid.openx', filterVideoBids(getOpenXContext()));
		context.set('bidders.prebid.ozone', filterVideoBids(getOzoneContext()));
		context.set('bidders.prebid.pubmatic', filterVideoBids(getPubmaticContext()));
		context.set('bidders.prebid.roundel', filterVideoBids(getRoundelContext()));
		context.set('bidders.prebid.rubicon', filterVideoBids(getRubiconContext()));
		context.set('bidders.prebid.rubicon_display', filterVideoBids(getRubiconDisplayContext()));
		context.set('bidders.prebid.seedtag', filterVideoBids(getSeedtagContext()));
		context.set('bidders.prebid.triplelift', filterVideoBids(getTripleliftContext()));
		context.set('bidders.prebid.verizon', filterVideoBids(getVerizonContext()));
		context.set('bidders.prebid.relevantdigital', filterVideoBids(getWebadsContext()));
		context.set('bidders.prebid.wikia', filterVideoBids(getWikiaContext()));
		context.set('bidders.prebid.wikiaVideo', filterVideoBids(getWikiaVideoContext()));

		this.applyFloorAdhesionExperimentBiddersIfApplicable();
	}

	private applyFloorAdhesionExperimentBiddersIfApplicable() {
		const expName = 'floorAdhesionWithBidders';
		if (context.get('experiments.floorAdhesionWithBidders')) {
			const isFloorAdhesionEligibleForRender = context.get(
				`experiments.${expName}.eligibleForRender`,
			);
			if (isFloorAdhesionEligibleForRender) {
				const prebidExperimentConfigs = context.get(
					`experiments.${expName}.bidderConfigurations.prebid`,
				);
				// For each of the prebid bidders that are configured in the experiment, take the bidder name and the associated floor_adhesion prebid slot configuration
				for (const [bidderName, floorAdhesionSlotConfig] of Object.entries(
					prebidExperimentConfigs,
				)) {
					// Just in case floor_adhesion is not present in the slot config, then bail.
					// This is just in case of a misconfiguration.
					if (!floorAdhesionSlotConfig?.['floor_adhesion']) {
						return;
					}
					// Take the current prebid configurations for the bidderName that's set in the prebid experiment configurations
					const existingBidderSlotConfigs = context.get(`bidders.prebid.${bidderName}.slots`);
					// Merge the slots definitions of the current prebid configuration, with the floor_adhesion slot definition.
					context.set(
						`bidders.prebid.${bidderName}.slots`,
						Object.assign(existingBidderSlotConfigs, floorAdhesionSlotConfig),
					);
				}
			}
		}
	}
}
